<script>
  import DpIcon from "./DpIcon";
  import reduce from 'lodash/reduce';
  import isEmpty from 'lodash/isEmpty';
  import DpNoDataIndicator from "./DpNoDataIndicator";

  export default {
    name: "DpTable",
    components: {DpNoDataIndicator, DpIcon},
    props: {
      size: {type: String, default: 'default'},
      data: {type: Array, required: true},
      columns: {type: [Array, Object], required: true},
      width: {type: String, default: "100%"},
      clickable: {type: Boolean, default: false},
      expandable: {type: Boolean, default: false},
      expandOnClick: {type: Boolean, default: true},
      slotPrefix: {type: String},
      reference: {type: String},
      border: {type: String},
      rtl: {type: Boolean, default: false}
    },
    data() {
      return {
        expanded: [],
        localCopyOfData: this.data,
        tableColumns: this.transformColumnsObject()
      };
    },
    computed: {
      isEmpty() {
        return isEmpty;
      }
    },
    methods: {
      expandRow(rowIndex) {
        this.reduceRow();
        this.$set(this.expanded, rowIndex, true);
      },
      reduceRow() {
        this.expanded = Array(this.data.length).fill(false);
      },
      transformColumnsObject() {
        if (typeof this.columns === 'object' && !(Array.isArray(this.columns))) {
          return reduce(this.columns, function (result, value, key) {
            result.push({prop: key, label: value});
            return result;
          }, []);
        } else {
          return this.columns;
        }
      }
    },
    mounted() {
      this.reduceRow();
    },
    watch: {
      data: {
        deep: true,
        handler(newData) {
          this.localCopyOfData = newData;
          this.reduceRow();
        }
      },
      columns: {
        deep: true,
        handler() {
          this.tableColumns = this.transformColumnsObject();
        }
      }
    }
  }
</script>

<template>
  <table
      :class="[
        'dp-table',
        `dp-table__${this.size}`,
        `dp-table__border__${this.border}`
      ]"
      :style="{
        'width':width
      }"
      :ref="(reference) ? reference : ''">
    <thead>
    <tr>
      <th v-for="(column, index) in tableColumns" :colspan="(expandable && index + 1 === tableColumns.length) ? 2 : 1"
          :key="index"
          :class="{
            'dp-table__align-left': column.align === 'left',
            'dp-table__align-right': column.align === 'right',
            'dp-table__align-center': column.align === 'center'
            }">
        {{(column.label) ? column.label : ''}}
      </th>
    </tr>
    </thead>
    <tbody>
    <template v-if="isEmpty(localCopyOfData)">
      <tr>
        <td :colspan="tableColumns.length+1">
          <DpNoDataIndicator/>
        </td>
      </tr>
    </template>
    <template v-else v-for="(row, rowIndex) in localCopyOfData" :key="(reference) ? reference+rowIndex.toString() : rowIndex">
      <tr :class="{
                    'dp-table__expanded': expanded[rowIndex]===true && expandable && !!$scopedSlots[rowIndex],
                    'dp-table__expandable': expandable && expandOnClick,
                    'dp-table__clickable': clickable,
                  }"
          @click="() => {
                        if(typeof row.action === 'function' && !!(row.action))
                          row.action();
                        if(expandable && expandOnClick && !!$scopedSlots[rowIndex])
                          (expanded[rowIndex]) ? reduceRow(rowIndex) : expandRow(rowIndex)
                      }">
        <td v-for="(cell, cellIndex) in tableColumns" :key="cellIndex"
            :class="{
                      'dp-table__align-left': cell.align && cell.align === 'left',
                      'dp-table__align-right': cell.align && cell.align === 'right',
                      'dp-table__align-center': cell.align && cell.align === 'center',
                      'dp-table__column-head': cell.head && cell.head === true
                    }">
          <slot :name="(slotPrefix) ? slotPrefix + cell.prop.toString() : cell.prop" v-bind:row="row">
            {{row[cell.prop]}}
          </slot>
        </td>
        <td v-if="expandable === true && !!($scopedSlots[rowIndex])" class="dp-table__align-right">
          <div v-if="expanded[rowIndex] === true" @click.native="reduceRow(rowIndex)" @keypress="reduceRow(rowIndex)"
               tabindex="0">
            <slot name="expand-controls-reduce">
              <DpIcon icon="chevron-block-start" style="cursor: pointer;"></DpIcon>
            </slot>
          </div>
          <div v-else @click.native="expandRow(rowIndex)" @keypress="expandRow(rowIndex)" tabindex="0">
            <slot name="expand-controls-expand">
              <DpIcon icon="chevron-block-end" style="cursor: pointer;"></DpIcon>
            </slot>
          </div>
        </td>
      </tr>
      <tr v-show="expanded[rowIndex]===true && expandable && !!$scopedSlots[rowIndex]">
        <td :colspan="tableColumns.length+1">
          <slot v-bind:row="row" :name="(slotPrefix) ? slotPrefix + rowIndex.toString() : rowIndex"></slot>
        </td>
      </tr>
    </template>
    </tbody>
  </table>
</template>

<style scoped lang="scss">

  .dp-table:not(.dp-table__border__no-outline) {
    overflow: hidden;
  }

  .dp-table__border__no-outline {
    border: 0 !important;
  }

  .dp-table__align-left {
    text-align: start;
  }

  .dp-table__align-right {
    text-align: end;
  }

  .dp-table__align-center {
    text-align: center;
  }

  .dp-table__column-head {
    color: #515151;
    font-size: 0.9em;
  }

  tr.dp-table__expanded {
    td {
      border-bottom: 0 !important;
    }
  }

  tr.dp-table__expandable,
  tr.dp-table__clickable {
    cursor: pointer;

    &:hover {
      background-color: #efefef;
    }
  }

</style>
